import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  alpha,
  useTheme,
  Paper,
  Card
} from '@mui/material';
import {
  Email as EmailIcon,
  Person as PersonIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import ActiveUsersTab from './user-settings-components/ActiveUsersTab';
import InvitationsTab from './user-settings-components/InvitationsTab';
import RolesAndPermissionsTab from './user-settings-components/RolesAndPermissionsTab';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`users-tabpanel-${index}`}
      aria-labelledby={`users-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function CompanyUsersSettings() {
  const theme = useTheme();
  const { companyId, canAccessPage } = usePermissions();
  const [activeTab, setActiveTab] = useState(0);
  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const [rolesCount, setRolesCount] = useState(0);

  // Define available tabs based on permissions
  const availableTabs = [
    {
      id: 0,
      label: "Active Users",
      icon: <PersonIcon />,
      permission: "active_users",
      component: <ActiveUsersTab />,
      hasAccess: canAccessPage('active_users')
    },
    {
      id: 1,
      label: "Invitations",
      icon: <EmailIcon />,
      permission: "invitations",
      component: <InvitationsTab />,
      hasAccess: canAccessPage('invitations')
    },
    {
      id: 2,
      label: "Roles & Permissions",
      icon: <SecurityIcon />,
      permission: "roles_permissions",
      component: <RolesAndPermissionsTab />,
      hasAccess: canAccessPage('roles_permissions')
    }
  ].filter(tab => tab.hasAccess);

  // Set initial tab to first available
  useEffect(() => {
    if (availableTabs.length > 0 && activeTab >= availableTabs.length) {
      setActiveTab(0);
    }
  }, [availableTabs.length, activeTab]);

  useEffect(() => {
    const fetchCounts = async () => {
      if (!companyId) return;

      try {
        // Get active users count
        const { data: users, error: usersError } = await supabase
          .from('company_users')
          .select('id')
          .eq('company_id', companyId)
          .eq('status', 'active');

        if (!usersError && users) {
          setActiveUsersCount(users.length);
        }

        // Get roles count (including system roles)
        const { data: roles, error: rolesError } = await supabase
          .from('roles')
          .select('id')
          .or(`company_id.eq.${companyId},is_system_role.eq.true`);

        if (!rolesError && roles) {
          setRolesCount(roles.length);
        }
      } catch (error) {
        console.error('Error fetching counts:', error);
      }
    };

    fetchCounts();
  }, [companyId]);

  // Check if user has any permissions at all
  if (availableTabs.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '70vh',
        textAlign: 'center',
        px: 3
      }}>
        <Card sx={{
          p: 8,
          borderRadius: 3,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
          maxWidth: 600,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          cursor: 'default',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
            opacity: 0.8,
            transition: 'opacity 0.3s ease'
          },
          '&:hover::before': {
            opacity: 1
          }
        }}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            mb: 6,
            display: 'inline-flex',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
          }}>
            <SecurityIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.5rem',
            letterSpacing: '-0.8px'
          }}>
            Access Restricted
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'text.secondary', 
            mb: 4,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}>
            User Management Permissions Required
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.7,
            fontSize: '1.05rem',
            maxWidth: 450,
            mx: 'auto'
          }}>
            You don't currently have the necessary permissions to access any User Management sections. 
            Contact your system administrator to request access or verify your role permissions.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* User Management Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        mb: 4,
        p: 4,
        borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          borderRadius: '50%',
          transform: 'translate(50%, -50%)',
          zIndex: 0
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.2rem',
            mb: 1,
            lineHeight: 1.2
          }}>
            User Management
          </Typography>
          
          <Typography variant="body1" sx={{ 
            color: 'text.secondary', 
            fontWeight: 500,
            fontSize: '1rem',
            mb: 3,
            maxWidth: '400px'
          }}>
            Manage team members, roles, and permissions with complete control
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Chip 
              label={`${activeUsersCount} Active Users`} 
              size="small" 
              sx={{ 
                bgcolor: alpha(theme.palette.info.main, 0.1),
                color: 'info.main',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
              }} 
            />
            <Chip 
              label={`${rolesCount} Available Roles`} 
              size="small" 
              sx={{ 
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: 'success.main',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
              }} 
            />
          </Box>
        </Box>


      </Box>

      {/* Navigation Tabs */}
      <Paper sx={{ 
        mb: 4,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ 
            px: 3,
            pt: 3,
            '& .MuiTab-root': {
              minHeight: 72,
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '0.9rem',
              px: 3,
              py: 2,
              mx: 1,
              borderRadius: 2,
              transition: 'all 0.2s ease-in-out',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 500
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              bottom: 0,
              borderRadius: '3px 3px 0 0',
              bgcolor: 'black',
              zIndex: 2
            }
          }}
        >
          {availableTabs.map((tab, index) => (
            <Tab 
              key={tab.permission}
              label={tab.label} 
              icon={tab.icon} 
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Card sx={{ 
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
        overflow: 'hidden',
        maxHeight: '80vh',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        },
        position: 'relative'
      }}>
        {availableTabs.map((tab, index) => (
          <TabPanel key={tab.permission} value={activeTab} index={index}>
            {tab.component}
          </TabPanel>
        ))}
      </Card>
    </Box>
  );
}
